/**
 * src/content/UsAtlasBiomeRegistry.ts
 * Complete 51-jurisdiction registry (50 US States + DC) mapped by official Census FIPS codes
 * matching `us-atlas/states-10m.json` and `us-atlas/counties-10m.json`.
 * Classifies every state into 4 tactical terrain biomes (FOREST, DESERT, PLAINS, SNOW),
 * defines real WGS84 GPS coordinates for telemetry simulation, and marks Northeast Callout states.
 */

export type TerrainBiomeId = 'FOREST' | 'DESERT' | 'PLAINS' | 'SNOW';

export interface BiomeVisualSpec {
  id: TerrainBiomeId;
  nameVi: string;
  nameEn: string;
  subtitleVi: string;
  patternId: string;
  activePatternId: string;
  baseColor: string;
  midColor: string;
  highColor: string;
  wallColorNear: string;
  wallColorMid: string;
  wallColorDeep: string;
  rimStroke: string;
  glowColor: string;
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  svgIcon: string;
}

export interface UsStateAtlasMeta {
  fips: string;
  code: string;
  name: string;
  vnName: string;
  capital: string;
  lat: number;
  lng: number;
  biome: TerrainBiomeId;
  elevationTier: 1 | 2 | 3;
  isFlagship?: boolean;
  isNortheastCallout?: boolean;
  calloutOrder?: number;
}

export const BIOME_SPECS: Record<TerrainBiomeId, BiomeVisualSpec> = {
  FOREST: {
    id: 'FOREST',
    nameVi: 'Rừng Nhiệt Đới & Lá Kim',
    nameEn: 'Emerald Canopy Forest',
    subtitleVi: 'Tây Bắc, Đông Bắc & Duyên hải Đông Nam',
    patternId: 'biome-surface-forest',
    activePatternId: 'biome-surface-forest-active',
    baseColor: '#0c2920',
    midColor: '#144937',
    highColor: '#1e6b50',
    wallColorNear: '#09221a',
    wallColorMid: '#071a14',
    wallColorDeep: '#04110d',
    rimStroke: '#34d399',
    glowColor: 'rgba(52, 211, 153, 0.45)',
    badgeBg: 'rgba(16, 185, 129, 0.14)',
    badgeBorder: 'rgba(52, 211, 153, 0.4)',
    badgeText: '#6ee7b7',
    svgIcon: `<svg viewBox="0 0 20 20" width="14" height="14" fill="none"><path d="M10 2.5L4.5 10h3.2L5 15.5h10L12.3 10h3.2L10 2.5z" fill="rgba(52,211,153,0.28)" stroke="#34d399" stroke-width="1.5" stroke-linejoin="round"/><path d="M10 15.5v2.5" stroke="#34d399" stroke-width="1.6" stroke-linecap="round"/></svg>`,
  },
  DESERT: {
    id: 'DESERT',
    nameVi: 'Sa Mạc & Hẻm Núi Đỏ',
    nameEn: 'Canyon Terracotta Desert',
    subtitleVi: 'Tây Nam, Thung lũng Lửa & Cao nguyên Đá Đỏ',
    patternId: 'biome-surface-desert',
    activePatternId: 'biome-surface-desert-active',
    baseColor: '#3b1c11',
    midColor: '#69321b',
    highColor: '#964926',
    wallColorNear: '#2d150c',
    wallColorMid: '#210f08',
    wallColorDeep: '#140904',
    rimStroke: '#f59e0b',
    glowColor: 'rgba(245, 158, 11, 0.45)',
    badgeBg: 'rgba(245, 158, 11, 0.15)',
    badgeBorder: 'rgba(251, 191, 36, 0.42)',
    badgeText: '#fcd34d',
    svgIcon: `<svg viewBox="0 0 20 20" width="14" height="14" fill="none"><path d="M2.5 15.5c3-2.5 5.5-2.5 8.5 0s4.5 2.5 6.5 0" stroke="#fbbf24" stroke-width="1.5" stroke-linecap="round"/><path d="M10 5v8M7 8.5v2.5h3M13 7.5v3h-3" stroke="#f59e0b" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><circle cx="14.8" cy="4.8" r="1.8" fill="rgba(251,191,36,0.35)" stroke="#fbbf24" stroke-width="1.2"/></svg>`,
  },
  PLAINS: {
    id: 'PLAINS',
    nameVi: 'Đồng Bằng & Thảo Nguyên',
    nameEn: 'Heartland Golden Plains',
    subtitleVi: 'Đại Đồng Bằng Trung Tâm & Vựa Lúa Mì',
    patternId: 'biome-surface-plains',
    activePatternId: 'biome-surface-plains-active',
    baseColor: '#252c15',
    midColor: '#434f24',
    highColor: '#617134',
    wallColorNear: '#1c220f',
    wallColorMid: '#14190a',
    wallColorDeep: '#0d1006',
    rimStroke: '#a3e635',
    glowColor: 'rgba(163, 230, 53, 0.42)',
    badgeBg: 'rgba(132, 204, 22, 0.15)',
    badgeBorder: 'rgba(163, 230, 53, 0.4)',
    badgeText: '#bef264',
    svgIcon: `<svg viewBox="0 0 20 20" width="14" height="14" fill="none"><path d="M2.5 13.5c3.5-1.8 7-1.8 10.5 0 2 .9 3.5.9 4.5.3" stroke="#a3e635" stroke-width="1.5" stroke-linecap="round"/><path d="M2.5 16.5c4-1.5 8-1.5 12 0 1.5.5 2.5.5 3 0" stroke="#84cc16" stroke-width="1.4" stroke-linecap="round"/><path d="M10 3.5v7M7.5 5.5L10 8l2.5-2.5M7.5 8.2L10 10.7l2.5-2.5" stroke="#bef264" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  },
  SNOW: {
    id: 'SNOW',
    nameVi: 'Núi Tuyết & Đỉnh Băng Giá',
    nameEn: 'Alpine Glacier & Rockies',
    subtitleVi: 'Dãy Rocky, Thượng Hồ & Cực Bắc Alaska',
    patternId: 'biome-surface-snow',
    activePatternId: 'biome-surface-snow-active',
    baseColor: '#152940',
    midColor: '#24476e',
    highColor: '#36699e',
    wallColorNear: '#102033',
    wallColorMid: '#0b1726',
    wallColorDeep: '#060d17',
    rimStroke: '#7dd3fc',
    glowColor: 'rgba(125, 211, 252, 0.48)',
    badgeBg: 'rgba(56, 189, 248, 0.15)',
    badgeBorder: 'rgba(125, 211, 252, 0.42)',
    badgeText: '#bae6fd',
    svgIcon: `<svg viewBox="0 0 20 20" width="14" height="14" fill="none"><path d="M2.5 15.5L8.2 5l3.6 6.2 2.4-4 3.3 8.3H2.5z" fill="rgba(125,211,252,0.22)" stroke="#7dd3fc" stroke-width="1.5" stroke-linejoin="round"/><path d="M6.4 8.3l1.8 1.2 1.8-1.2" stroke="#f0f9ff" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  },
};

/**
 * Northeast Micro-States ordered from North to South for right-hand Atlantic Callout Rail
 */
export const NORTHEAST_CALLOUT_CODES = [
  'NH',
  'VT',
  'MA',
  'RI',
  'CT',
  'NJ',
  'DE',
  'MD',
  'DC',
] as const;

export const US_STATES_ATLAS_REGISTRY: Record<string, UsStateAtlasMeta> = {
  '01': { fips: '01', code: 'AL', name: 'Alabama', vnName: 'Rừng thông miền Nam & Vịnh Mobile', capital: 'Montgomery', lat: 32.3792, lng: -86.3077, biome: 'FOREST', elevationTier: 1 },
  '02': { fips: '02', code: 'AK', name: 'Alaska', vnName: 'Vùng băng tuyết vĩnh cửu & Đỉnh Denali', capital: 'Juneau / Anchorage', lat: 61.2181, lng: -149.9003, biome: 'SNOW', elevationTier: 3 },
  '04': { fips: '04', code: 'AZ', name: 'Arizona', vnName: 'Đại Vực Grand Canyon & Sa mạc Sonoran', capital: 'Phoenix', lat: 33.4484, lng: -112.0740, biome: 'DESERT', elevationTier: 2 },
  '05': { fips: '05', code: 'AR', name: 'Arkansas', vnName: 'Rừng núi Ozark & Suối khoáng nóng', capital: 'Little Rock', lat: 34.7465, lng: -92.2896, biome: 'FOREST', elevationTier: 1 },
  '06': { fips: '06', code: 'CA', name: 'California', vnName: 'Thung lũng Silicon, Bờ Tây & Sa mạc Mojave', capital: 'San Francisco / LA', lat: 36.7783, lng: -119.4179, biome: 'DESERT', elevationTier: 3, isFlagship: true },
  '08': { fips: '08', code: 'CO', name: 'Colorado', vnName: 'Đỉnh núi tuyết Rocky Mountains & Aspen', capital: 'Denver', lat: 39.7392, lng: -104.9903, biome: 'SNOW', elevationTier: 3 },
  '09': { fips: '09', code: 'CT', name: 'Connecticut', vnName: 'Bờ biển New England & Đại học Yale', capital: 'Hartford', lat: 41.6032, lng: -72.6851, biome: 'FOREST', elevationTier: 1, isNortheastCallout: true, calloutOrder: 5 },
  '10': { fips: '10', code: 'DE', name: 'Delaware', vnName: 'Vịnh Delaware & Thủ phủ Tài chính Doanh nghiệp', capital: 'Dover', lat: 39.1582, lng: -75.5244, biome: 'FOREST', elevationTier: 1, isNortheastCallout: true, calloutOrder: 7 },
  '11': { fips: '11', code: 'DC', name: 'District of Columbia', vnName: 'Thủ đô Liên bang Washington D.C.', capital: 'Washington, D.C.', lat: 38.9072, lng: -77.0369, biome: 'FOREST', elevationTier: 2, isNortheastCallout: true, calloutOrder: 9 },
  '12': { fips: '12', code: 'FL', name: 'Florida', vnName: 'Bán đảo Nhiệt đới, Everglades & Mũi Tên lửa', capital: 'Miami / Cape Canaveral', lat: 27.9944, lng: -81.7603, biome: 'FOREST', elevationTier: 3, isFlagship: true },
  '13': { fips: '13', code: 'GA', name: 'Georgia', vnName: 'Rừng thông Blue Ridge & Trung tâm Atlanta', capital: 'Atlanta', lat: 33.7490, lng: -84.3880, biome: 'FOREST', elevationTier: 2 },
  '15': { fips: '15', code: 'HI', name: 'Hawaii', vnName: 'Quần đảo Nhiệt đới & Núi lửa Thái Bình Dương', capital: 'Honolulu', lat: 21.3069, lng: -157.8583, biome: 'FOREST', elevationTier: 2 },
  '16': { fips: '16', code: 'ID', name: 'Idaho', vnName: 'Dãy núi tuyết Sawtooth & Hẻm sông Snake', capital: 'Boise', lat: 43.6150, lng: -116.2023, biome: 'SNOW', elevationTier: 2 },
  '17': { fips: '17', code: 'IL', name: 'Illinois', vnName: 'Thảo nguyên Trung Tây & Siêu đô thị Chicago', capital: 'Chicago', lat: 40.6331, lng: -89.3985, biome: 'PLAINS', elevationTier: 2 },
  '18': { fips: '18', code: 'IN', name: 'Indiana', vnName: 'Đồng bằng Wabash & Đường đua Indianapolis', capital: 'Indianapolis', lat: 39.7684, lng: -86.1581, biome: 'PLAINS', elevationTier: 1 },
  '19': { fips: '19', code: 'IA', name: 'Iowa', vnName: 'Đồng bằng nông nghiệp trù phú giữa hai dòng sông', capital: 'Des Moines', lat: 41.5868, lng: -93.6250, biome: 'PLAINS', elevationTier: 1 },
  '20': { fips: '20', code: 'KS', name: 'Kansas', vnName: 'Trái tim Đại Đồng Bằng & Cánh đồng lúa mì vàng', capital: 'Topeka / Wichita', lat: 38.5266, lng: -96.7265, biome: 'PLAINS', elevationTier: 1 },
  '21': { fips: '21', code: 'KY', name: 'Kentucky', vnName: 'Đồi cỏ Bluegrass & Rừng Appalachia', capital: 'Frankfort', lat: 37.8393, lng: -84.2700, biome: 'FOREST', elevationTier: 1 },
  '22': { fips: '22', code: 'LA', name: 'Louisiana', vnName: 'Châu thổ sông Mississippi & Đầm lầy Bayou', capital: 'New Orleans', lat: 30.9843, lng: -91.9623, biome: 'FOREST', elevationTier: 1 },
  '23': { fips: '23', code: 'ME', name: 'Maine', vnName: 'Rừng tùng Acadia & Bờ biển đá Đại Tây Dương', capital: 'Augusta', lat: 45.2538, lng: -69.4455, biome: 'FOREST', elevationTier: 2 },
  '24': { fips: '24', code: 'MD', name: 'Maryland', vnName: 'Vịnh Chesapeake & Cảng lịch sử Baltimore', capital: 'Annapolis', lat: 39.0458, lng: -76.6413, biome: 'FOREST', elevationTier: 1, isNortheastCallout: true, calloutOrder: 8 },
  '25': { fips: '25', code: 'MA', name: 'Massachusetts', vnName: 'Vịnh Massachusetts, Boston & Viện MIT/Harvard', capital: 'Boston', lat: 42.3601, lng: -71.5589, biome: 'FOREST', elevationTier: 2, isNortheastCallout: true, calloutOrder: 3 },
  '26': { fips: '26', code: 'MI', name: 'Michigan', vnName: 'Bán đảo Ngũ Đại Hồ & Rừng Thượng Michigan', capital: 'Lansing / Detroit', lat: 43.3266, lng: -84.5361, biome: 'FOREST', elevationTier: 2 },
  '27': { fips: '27', code: 'MN', name: 'Minnesota', vnName: 'Vùng băng giá 10.000 Hồ & Rừng Bắc Cực', capital: 'Saint Paul', lat: 46.0296, lng: -94.6859, biome: 'SNOW', elevationTier: 2 },
  '28': { fips: '28', code: 'MS', name: 'Mississippi', vnName: 'Rừng tràm Magnolia & Dòng sông Mississippi', capital: 'Jackson', lat: 32.3547, lng: -89.3985, biome: 'FOREST', elevationTier: 1 },
  '29': { fips: '29', code: 'MO', name: 'Missouri', vnName: 'Cổng vòm Gateway Arch & Đồng bằng Ozark', capital: 'Jefferson City', lat: 38.5767, lng: -92.1735, biome: 'PLAINS', elevationTier: 1 },
  '30': { fips: '30', code: 'MT', name: 'Montana', vnName: 'Vườn quốc gia Sông băng Glacier & Đỉnh Rocky', capital: 'Helena', lat: 46.8797, lng: -110.3626, biome: 'SNOW', elevationTier: 3 },
  '31': { fips: '31', code: 'NE', name: 'Nebraska', vnName: 'Thảo nguyên Sandhills & Thung lũng sông Platte', capital: 'Lincoln', lat: 41.4925, lng: -99.9018, biome: 'PLAINS', elevationTier: 1 },
  '32': { fips: '32', code: 'NV', name: 'Nevada', vnName: 'Sa mạc Great Basin & Ốc đảo ánh sáng Las Vegas', capital: 'Carson City / Las Vegas', lat: 38.8026, lng: -116.4194, biome: 'DESERT', elevationTier: 2 },
  '33': { fips: '33', code: 'NH', name: 'New Hampshire', vnName: 'Đỉnh núi đá Granite & Rừng phong White Mountains', capital: 'Concord', lat: 43.4525, lng: -71.5639, biome: 'FOREST', elevationTier: 2, isNortheastCallout: true, calloutOrder: 1 },
  '34': { fips: '34', code: 'NJ', name: 'New Jersey', vnName: 'Hành lang Đại Tây Dương & Bờ biển Jersey Shore', capital: 'Trenton', lat: 40.0583, lng: -74.4057, biome: 'FOREST', elevationTier: 1, isNortheastCallout: true, calloutOrder: 6 },
  '35': { fips: '35', code: 'NM', name: 'New Mexico', vnName: 'Sa mạc Cát Trắng White Sands & Cao nguyên Santa Fe', capital: 'Santa Fe', lat: 34.5199, lng: -105.8701, biome: 'DESERT', elevationTier: 2 },
  '36': { fips: '36', code: 'NY', name: 'New York', vnName: 'Siêu đô thị Manhattan, Dãy Adirondacks & Thác Niagara', capital: 'New York City / Albany', lat: 42.6526, lng: -75.5000, biome: 'FOREST', elevationTier: 3, isFlagship: true },
  '37': { fips: '37', code: 'NC', name: 'North Carolina', vnName: 'Rừng sương mù Great Smoky & Quần đảo Outer Banks', capital: 'Raleigh', lat: 35.7596, lng: -79.0193, biome: 'FOREST', elevationTier: 2 },
  '38': { fips: '38', code: 'ND', name: 'North Dakota', vnName: 'Thảo nguyên Badlands & Đồng bằng phương Bắc', capital: 'Bismarck', lat: 47.5515, lng: -101.0020, biome: 'PLAINS', elevationTier: 1 },
  '39': { fips: '39', code: 'OH', name: 'Ohio', vnName: 'Đồng bằng Hồ Erie & Thung lũng sông Ohio', capital: 'Columbus', lat: 39.9612, lng: -82.9988, biome: 'PLAINS', elevationTier: 1 },
  '40': { fips: '40', code: 'OK', name: 'Oklahoma', vnName: 'Thảo nguyên đất đỏ Red Plains & Giao lộ Route 66', capital: 'Oklahoma City', lat: 35.4676, lng: -97.5164, biome: 'PLAINS', elevationTier: 1 },
  '41': { fips: '41', code: 'OR', name: 'Oregon', vnName: 'Rừng tùng Cascade & Hồ miệng núi lửa Crater Lake', capital: 'Salem / Portland', lat: 43.8041, lng: -120.5542, biome: 'FOREST', elevationTier: 2 },
  '42': { fips: '42', code: 'PA', name: 'Pennsylvania', vnName: 'Rừng Allegheny, Philadelphia & Cái nôi Lập quốc', capital: 'Harrisburg / Philadelphia', lat: 40.9000, lng: -77.7996, biome: 'FOREST', elevationTier: 2 },
  '44': { fips: '44', code: 'RI', name: 'Rhode Island', vnName: 'Tiểu bang Đại Dương & Vịnh thuyền buồm Newport', capital: 'Providence', lat: 41.6809, lng: -71.5118, biome: 'FOREST', elevationTier: 1, isNortheastCallout: true, calloutOrder: 4 },
  '45': { fips: '45', code: 'SC', name: 'South Carolina', vnName: 'Rừng cọ Palmetto & Phố cảng cổ Charleston', capital: 'Columbia', lat: 33.8361, lng: -81.1637, biome: 'FOREST', elevationTier: 1 },
  '46': { fips: '46', code: 'SD', name: 'South Dakota', vnName: 'Đài tưởng niệm Mount Rushmore & Thảo nguyên Black Hills', capital: 'Pierre', lat: 44.3683, lng: -100.3510, biome: 'PLAINS', elevationTier: 2 },
  '47': { fips: '47', code: 'TN', name: 'Tennessee', vnName: 'Dãy núi Great Smoky & Thủ phủ âm nhạc Nashville', capital: 'Nashville', lat: 35.8601, lng: -86.6602, biome: 'FOREST', elevationTier: 2 },
  '48': { fips: '48', code: 'TX', name: 'Texas', vnName: 'Cao nguyên Lone Star, Sa mạc Big Bend & Vịnh Mexico', capital: 'Austin / Houston', lat: 31.4686, lng: -99.3318, biome: 'DESERT', elevationTier: 3, isFlagship: true },
  '49': { fips: '49', code: 'UT', name: 'Utah', vnName: 'Vòm đá đỏ Arches, Hẻm núi Zion & Hồ Muối Lớn', capital: 'Salt Lake City', lat: 39.3210, lng: -111.0937, biome: 'DESERT', elevationTier: 2 },
  '50': { fips: '50', code: 'VT', name: 'Vermont', vnName: 'Dãy núi Xanh Green Mountains & Rừng phong lá đỏ', capital: 'Montpelier', lat: 44.0588, lng: -72.5778, biome: 'FOREST', elevationTier: 2, isNortheastCallout: true, calloutOrder: 2 },
  '51': { fips: '51', code: 'VA', name: 'Virginia', vnName: 'Thung lũng Shenandoah & Bờ biển Chesapeake', capital: 'Richmond', lat: 37.4316, lng: -78.6569, biome: 'FOREST', elevationTier: 2 },
  '53': { fips: '53', code: 'WA', name: 'Washington', vnName: 'Rừng mưa Olympic, Đỉnh Rainier & Vịnh Puget Sound', capital: 'Seattle / Olympia', lat: 47.4009, lng: -120.5015, biome: 'FOREST', elevationTier: 3, isFlagship: true },
  '54': { fips: '54', code: 'WV', name: 'West Virginia', vnName: 'Tiểu bang Núi rừng Appalachia & Hẻm sông New River', capital: 'Charleston', lat: 38.5976, lng: -80.4549, biome: 'FOREST', elevationTier: 2 },
  '55': { fips: '55', code: 'WI', name: 'Wisconsin', vnName: 'Rừng Bắc Bộ & Thảo nguyên ven Hồ Michigan', capital: 'Madison', lat: 44.5000, lng: -89.5000, biome: 'FOREST', elevationTier: 1 },
  '56': { fips: '56', code: 'WY', name: 'Wyoming', vnName: 'Đỉnh tuyết Grand Teton & Địa nhiệt Yellowstone', capital: 'Cheyenne', lat: 43.0760, lng: -107.2903, biome: 'SNOW', elevationTier: 3 },
};

export const STATE_BY_CODE: Record<string, UsStateAtlasMeta> = Object.values(
  US_STATES_ATLAS_REGISTRY
).reduce((acc, item) => {
  acc[item.code] = item;
  return acc;
}, {} as Record<string, UsStateAtlasMeta>);

export const STATE_BY_FIPS: Record<string, UsStateAtlasMeta> = US_STATES_ATLAS_REGISTRY;

export function resolveStateAtlasMeta(stateCodeOrFips: string): UsStateAtlasMeta | undefined {
  if (!stateCodeOrFips) return undefined;
  const clean = stateCodeOrFips.trim().toUpperCase();
  if (STATE_BY_CODE[clean]) return STATE_BY_CODE[clean];
  const paddedFips = clean.padStart(2, '0');
  return US_STATES_ATLAS_REGISTRY[paddedFips];
}

export interface GpsPresetLocation {
  code: string;
  label: string;
  city: string;
  lat: number;
  lng: number;
  tag: string;
}

export const GPS_SIMULATOR_PRESETS: GpsPresetLocation[] = [
  { code: 'NY', label: 'New York', city: 'Manhattan, NY', lat: 42.6526, lng: -75.5000, tag: 'Flagship • Rừng' },
  { code: 'CA', label: 'California', city: 'Silicon Valley, CA', lat: 36.7783, lng: -119.4179, tag: 'Flagship • Sa mạc' },
  { code: 'TX', label: 'Texas', city: 'Austin, TX', lat: 31.4686, lng: -99.3318, tag: 'Flagship • Sa mạc' },
  { code: 'FL', label: 'Florida', city: 'Cape Canaveral, FL', lat: 27.9944, lng: -81.7603, tag: 'Flagship • Rừng' },
  { code: 'WA', label: 'Washington', city: 'Seattle, WA', lat: 47.4009, lng: -120.5015, tag: 'Flagship • Rừng' },
  { code: 'CO', label: 'Colorado', city: 'Aspen Rockies, CO', lat: 39.7392, lng: -104.9903, tag: 'Núi tuyết' },
  { code: 'AK', label: 'Alaska', city: 'Anchorage, AK', lat: 61.2181, lng: -149.9003, tag: 'Núi tuyết' },
  { code: 'AZ', label: 'Arizona', city: 'Grand Canyon, AZ', lat: 33.4484, lng: -112.0740, tag: 'Sa mạc' },
  { code: 'KS', label: 'Kansas', city: 'Heartland, KS', lat: 38.5266, lng: -96.7265, tag: 'Đồng bằng' },
  { code: 'MA', label: 'Massachusetts', city: 'Boston, MA', lat: 42.3601, lng: -71.5589, tag: 'Đông Bắc Callout' },
  { code: 'DC', label: 'Washington D.C.', city: 'Capitol Hill, DC', lat: 38.9072, lng: -77.0369, tag: 'Đông Bắc Callout' },
  { code: 'HI', label: 'Hawaii', city: 'Honolulu, HI', lat: 21.3069, lng: -157.8583, tag: 'Quần đảo' },
];
